/**
 * Wrap a state-changing callback so that the page's vertical scroll
 * position is preserved across the resulting layout shift.
 *
 * Use case: clicking on a tab, expanding a row, or any other
 * interaction whose new state changes the height of content above
 * (or to the side of) the user's viewport. Without this guard the
 * browser silently clamps `scrollY` when the document grows shorter,
 * or focus-into-view scrolls a newly-mounted element out of place,
 * and the user perceives the page as "jumping".
 *
 * Implementation:
 * 1. Snapshot `window.scrollY` before invoking the action.
 * 2. Run the action (typically a `setState` that triggers a re-render).
 * 3. On the next animation frame — after React has committed the new
 *    DOM — check whether `scrollY` drifted by more than 1px. If so,
 *    restore it explicitly with an instant scroll. Identical positions
 *    skip the call so the browser's smooth scrolling isn't interrupted.
 *
 * Use:
 * ```tsx
 *   const handleTabClick = (key: string) => preserveScroll(() => setActiveTab(key));
 *
 *   // Or as a wrapper around an existing handler:
 *   onClick={() => preserveScroll(onChange.bind(null, key))}
 * ```
 *
 * Notes:
 * - The action runs synchronously; the restore is async (rAF). If the
 *   action itself is async, scroll preservation only covers the first
 *   render — chained renders may still drift. Wrap the inner state
 *   update directly in that case.
 * - Safe in SSR: guards against `typeof window === 'undefined'`.
 */
export function preserveScroll<T>(action: () => T): T {
    if (typeof window === 'undefined') return action();
    const y = window.scrollY;
    const result = action();
    requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - y) > 1) {
            window.scrollTo({ top: y, behavior: 'instant' as ScrollBehavior });
        }
    });
    return result;
}
